import { EventInput } from '@fullcalendar/core'
import { Dayjs } from 'dayjs'

import { getISOStringWithoutTime, getUtcDayjs, isWeekend } from './datetime'

export const dataSourceToFullCalendarEvents = <T>(
  datasource: T[],
  extractDate: (record: T) => string
): EventInput[] => {
  if (!datasource.length) {
    return []
  }

  const events = datasource.map((record) => {
    const dateWithoutTime = getISOStringWithoutTime(getUtcDayjs(extractDate(record)))
    return {
      title: JSON.stringify(record),
      start: dateWithoutTime,
      end: dateWithoutTime,
    }
  })
  return events
}

export const ensureWeekdayRecordsForDateRange = <T>(
  datasource: T[],
  extractDate: (record: T) => string,
  startDate: string,
  endDate: string,
  createDefaultRecord: (dateWithoutTime: string) => T
): T[] => {
  const recordMap = new Map<string, T>()
  datasource.forEach((record) => {
    const dateWithoutTime = getISOStringWithoutTime(getUtcDayjs(extractDate(record)))
    recordMap.set(dateWithoutTime, record)
  })

  const recordsWithDefaults: T[] = []
  let currentDate = getUtcDayjs(startDate).startOf('day')
  const rangeEndDate = getUtcDayjs(endDate).startOf('day')

  while (currentDate.isBefore(rangeEndDate) || currentDate.isSame(rangeEndDate, 'day')) {
    if (!isWeekend(currentDate)) {
      const dateWithoutTime = getISOStringWithoutTime(currentDate)
      recordsWithDefaults.push(
        recordMap.get(dateWithoutTime) ?? createDefaultRecord(dateWithoutTime)
      )
    }
    currentDate = currentDate.add(1, 'day')
  }

  return recordsWithDefaults
}

const moveBackToDayOfWeek = (d: Dayjs, targetDayOfWeek: number): Dayjs => {
  let newDate = d.clone()
  while (newDate.day() !== targetDayOfWeek) {
    newDate = newDate.subtract(1, 'day')
  }
  return newDate
}

const moveForwardToDayOfWeek = (d: Dayjs, targetDayOfWeek: number): Dayjs => {
  let newDate = d.clone()
  while (newDate.day() !== targetDayOfWeek) {
    newDate = newDate.add(1, 'day')
  }
  return newDate
}

export function getCalendarBoundaries(selectedMonth: Dayjs) {
  let startDate = selectedMonth.clone().startOf('month')

  if (isWeekend(startDate)) {
    startDate = moveForwardToDayOfWeek(startDate, 1)
  } else {
    startDate = moveBackToDayOfWeek(startDate, 1)
  }

  let endDate = selectedMonth.clone().endOf('month')
  if (isWeekend(endDate)) {
    endDate = moveBackToDayOfWeek(endDate, 5)
  } else {
    endDate = moveForwardToDayOfWeek(endDate, 5)
  }

  return {
    start_date: getISOStringWithoutTime(startDate),
    end_date: getISOStringWithoutTime(endDate),
  }
}
